import { Link as RouterLink } from 'react-router-dom';

import { LWApiPublicNotificationDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/notifications/index.raw';
import Box from '@liquidweb-dev/undercurrent/Box';
import ListItem, {
	type ListItemProps,
} from '@liquidweb-dev/undercurrent/ListItem';
import ListItemButton from '@liquidweb-dev/undercurrent/ListItemButton';
import { pseudoClasses } from '@liquidweb-dev/undercurrent/classes/pseudo';
import type { Theme } from '@liquidweb-dev/undercurrent/styles';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import ActionsMenu from 'components/HostingDetailsActions/ActionsMenu';
import ActionsSlot from 'components/ServersList/ActionsSlot';
import getRowColor from 'components/ServersList/getRowColor';
import NutanixDetails from 'components/ServersList/NutanixDetails';
import RowDetailsContainer from 'components/ServersList/RowDetailsContainer';
import RowIdentity from 'components/ServersList/RowIdentity';

export interface ServersListRowProps extends ListItemProps {
	assetDetails: AssetDetails;
	notification?: LWApiPublicNotificationDetailsResultRawI;
	/** Set for a nested Nutanix VPC; other rows pass it through their own details. */
	numberOfChildServers?: number;
	/** Indents the row's content under its parent while the row, and its hover state, still span the list. */
	isNested?: boolean;
}

const StandardRow = ({
	assetDetails,
	notification,
	isNested,
	children,
	...rest
}: ServersListRowProps) => {
	const { path, powerStatus, status } = assetDetails;

	return (
		<ListItem color={getRowColor({ status, powerStatus })} {...rest}>
			<ListItemButton
				component={RouterLink}
				to={path}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 300,
					[pseudoClasses.important]: {
						padding: 200,
						...(isNested && {
							paddingLeft: ({ size, spacing }: Theme) =>
								`calc(${spacing(200)} + ${size.md})`,
						}),
					},
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 300,
						color: 'initial',
						textDecoration: 'none',
						flex: '1 0 0',
						minWidth: 0,
					}}
				>
					<RowIdentity
						assetDetails={assetDetails}
						notification={notification}
					/>

					<RowDetailsContainer>{children}</RowDetailsContainer>
				</Box>

				<Box
					onClick={(e) => {
						// prevents all events from bubbling up to the list item button (which would cause the link to be followed)
						e.stopPropagation();
					}}
				>
					<ActionsSlot assetDetails={assetDetails}>
						<ActionsMenu
							{...assetDetails}
							intent="serversList"
							iconButtonProps={{
								onClick: (e) => {
									// for some reason also need to prevent default on the button to prevent the link from being followed
									e.preventDefault();
								},
								variant: 'ghost',
								size: 'small',
								sx: {
									flexShrink: 0,
									flexGrow: 0,
								},
							}}
						/>
					</ActionsSlot>
				</Box>
			</ListItemButton>
		</ListItem>
	);
};

/**
 * A server list row. A Nutanix Private Cloud asset shows its own details block
 * in place of the caller's, and is otherwise presented like every other row.
 */
const ServersListRow = ({
	assetDetails,
	children,
	numberOfChildServers,
	isNested,
	...rest
}: ServersListRowProps) => (
	<StandardRow assetDetails={assetDetails} isNested={isNested} {...rest}>
		{assetDetails.isNutanixPrivateCloud ? (
			<NutanixDetails
				assetDetails={assetDetails}
				numberOfChildServers={numberOfChildServers}
			/>
		) : (
			children
		)}
	</StandardRow>
);

export default ServersListRow;
